import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

// Shared state for the Setup live-preview panel. Each Setup section streams its
// current (unsaved) form values as a guideInfo-shaped partial via updatePreview();
// these are deep-merged into a single draft that LivePreviewPanel posts to the
// embedded tipping iframe. Also tracks which section the operator is focused on
// (so the preview can navigate to the matching screen) and whether the iframe
// has completed its handshake.

const deepMerge = (base, patch) => {
  if (Array.isArray(patch) || patch === null || typeof patch !== 'object') {
    return patch;
  }
  const out = { ...(base && typeof base === 'object' ? base : {}) };
  Object.keys(patch).forEach(key => {
    out[key] = deepMerge(out[key], patch[key]);
  });
  return out;
};

const SetupPreviewContext = createContext(null);

// Post-tip flows (social, upsell, custom links) are gated behind the company's
// subscription tier on the tipping page. Since the operator can author all of
// them in Setup regardless of plan, the preview unlocks them so the config they
// just entered is actually visible. The tipping page still applies its DATA gates
// (a flow only shows if it has content), so this reveals exactly what's configured
// — nothing more. Preview-only; never affects real customer sessions.
const PREVIEW_FEATURE_UNLOCK = {
  pricingSubscription: {
    combinedFeatures: {
      PostTipSocialLinks: true,
      PostTipUpsell: true,
      PostTipExternalLink: true,
      PostTipReviewCollection: true
    }
  }
};

export function SetupPreviewProvider({ children }) {
  const [draft, setDraft] = useState(PREVIEW_FEATURE_UNLOCK);
  const [activeSection, setActiveSection] = useState(null);
  const iframeReadyRef = useRef(false);
  const [iframeReady, setIframeReadyState] = useState(false);

  // Merge a guideInfo-shaped partial (e.g. { branding: {...} } or
  // { features: { postTipPage: { upsell: {...} } } }) into the running draft.
  const updatePreview = useCallback(partial => {
    if (!partial) return;
    setDraft(prev => deepMerge(prev, partial));
  }, []);

  const setIframeReady = useCallback(ready => {
    iframeReadyRef.current = ready;
    setIframeReadyState(ready);
  }, []);

  const value = useMemo(
    () => ({
      draft,
      updatePreview,
      activeSection,
      setActiveSection,
      iframeReady,
      setIframeReady
    }),
    [draft, updatePreview, activeSection, iframeReady, setIframeReady]
  );

  return <SetupPreviewContext.Provider value={value}>{children}</SetupPreviewContext.Provider>;
}

// Safe outside a provider — sections still work when the preview isn't mounted
// (e.g. below the xl breakpoint), updatePreview is then a no-op.
export function useSetupPreview() {
  return (
    useContext(SetupPreviewContext) || {
      draft: {},
      updatePreview: () => {},
      activeSection: null,
      setActiveSection: () => {},
      iframeReady: false,
      setIframeReady: () => {}
    }
  );
}

export default SetupPreviewContext;
